import { useState, useEffect, useRef } from 'react'
import { Download, FileText, Calendar, Filter, Users, X, FileIcon, Clock, Search, ChevronDown, Check, Hash, Trophy } from 'lucide-react'
import api from '../../services/api'
import { jsPDF } from 'jspdf'

interface DateInfo {
  date: string
  day: number
  weekday: string
}

interface DailyIndicator {
  submission_id: number
  indicator_id: number
  code: string
  name: string
}

interface IndicatorBreakdown {
  indicator_id: number
  code: string
  name: string
  target: number
  max_points: number
  submissions: number
  points: number
}

interface Employee {
  user: {
    id: number
    name: string
    employee_id: string | null
  }
  daily_submissions: { [date: string]: number }
  daily_points: { [date: string]: number }
  daily_breakdown: { [date: string]: DailyIndicator[] }
  accumulated_points: { [date: string]: number }
  total_submissions: number
  total_points: number
  indicator_breakdown: IndicatorBreakdown[]
}

interface Indicator {
  id: number
  code: string
  name: string
  target: number
}

interface Period {
  id: number | null
  year: number
  month: number
  name: string
  start_date?: string
  end_date?: string
}

interface CalendarReportData {
  period: Period
  start_date: string
  end_date: string
  dates: DateInfo[]
  employees: Employee[]
  indicators: Indicator[]
  selected_indicator: Indicator | null
}

interface Attachment {
  id: number
  filename: string
  original_filename: string
  file_path: string
  file_type: string
  file_size: number
  ai_classification: string | null
  ai_confidence: number | null
  ai_is_valid: boolean | null
}

interface SubmissionDetail {
  id: number
  indicator: {
    id: number
    code: string
    name: string
  } | null
  submission_date: string
  value: number
  notes: string | null
  status: string
  attachments: Attachment[]
  created_at: string
}

interface SubmissionDetailsData {
  user: {
    id: number
    name: string
    employee_id: string | null
  }
  date: string
  submissions: SubmissionDetail[]
  total_count: number
}

interface UserOption {
  id: number
  name: string
  employee_id?: string
}

interface IndicatorOption {
  id: number
  code: string
  name: string
}

// Month names in Indonesian
const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
]

export default function ReportsPage() {
  // Filter state
  const [filterMode, setFilterMode] = useState<'monthly' | 'dateRange'>('monthly')
  const [viewMode, setViewMode] = useState<'submissions' | 'points'>('submissions')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedIndicator, setSelectedIndicator] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  // Data state
  const [reportData, setReportData] = useState<CalendarReportData | null>(null)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<UserOption[]>([])
  const [indicators, setIndicators] = useState<IndicatorOption[]>([])

  // Employee search dropdown state
  const [userSearchOpen, setUserSearchOpen] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const userSearchRef = useRef<HTMLDivElement>(null)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [submissionDetails, setSubmissionDetails] = useState<SubmissionDetailsData | null>(null)

  // Image preview state
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  // Employee breakdown dropdown state
  const [expandedEmployee, setExpandedEmployee] = useState<number | null>(null)

  // Export state
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)

  // Generate year options (last 5 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  // Fetch users and indicators on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users?role=supervisor&is_active=true&per_page=1000')
        setUsers(response.data.items || response.data)
      } catch (error) {
        console.error('Failed to fetch users:', error)
      }
    }

    const fetchIndicators = async () => {
      try {
        const response = await api.get('/kpi/indicators')
        setIndicators(response.data.items || response.data)
      } catch (error) {
        console.error('Failed to fetch indicators:', error)
      }
    }

    fetchUsers()
    fetchIndicators()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setUserSearchOpen(false)
      }
      // Close employee breakdown dropdown if clicking outside
      const target = event.target as HTMLElement
      if (!target.closest('.employee-breakdown-dropdown') && !target.closest('.employee-name-button')) {
        setExpandedEmployee(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset expanded employee when indicator filter changes
  useEffect(() => {
    setExpandedEmployee(null)
  }, [selectedIndicator])

  // Fetch report when filters change
  useEffect(() => {
    const fetchCalendarReport = async () => {
      setLoading(true)
      try {
        let url = '/reports/calendar?'
        const params: string[] = []

        if (filterMode === 'monthly') {
          params.push(`year=${selectedYear}`)
          params.push(`month=${selectedMonth}`)
        } else if (startDate && endDate) {
          params.push(`start_date=${startDate}`)
          params.push(`end_date=${endDate}`)
        } else {
          setLoading(false)
          return
        }

        if (selectedIndicator) {
          params.push(`indicator_id=${selectedIndicator}`)
        }

        // Only filter by first selected user for now (backend limitation)
        if (selectedUsers.length === 1) {
          params.push(`user_id=${selectedUsers[0]}`)
        }

        url += params.join('&')
        const response = await api.get(url)

        // If multiple users selected, filter client-side
        if (selectedUsers.length > 1) {
          response.data.employees = response.data.employees.filter(
            (emp: Employee) => selectedUsers.includes(emp.user.id)
          )
        }

        setReportData(response.data)
      } catch (error) {
        console.error('Failed to fetch calendar report:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCalendarReport()
  }, [filterMode, selectedYear, selectedMonth, startDate, endDate, selectedIndicator, selectedUsers])

  const handleCellClick = async (userId: number, date: string, count: number) => {
    if (count === 0) return

    setShowModal(true)
    setModalLoading(true)
    setSubmissionDetails(null)

    try {
      let url = `/reports/submission-details?user_id=${userId}&date=${date}`
      if (selectedIndicator) {
        url += `&indicator_id=${selectedIndicator}`
      }
      const response = await api.get(url)
      setSubmissionDetails(response.data)
    } catch (error) {
      console.error('Failed to fetch submission details:', error)
    } finally {
      setModalLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (exporting) return
    setExporting('excel')
    try {
      let url = '/reports/export/excel?'
      if (filterMode === 'monthly') {
        url += `year=${selectedYear}&month=${selectedMonth}`
      }
      const response = await api.get(url, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `KPI_Report_${selectedYear}_${selectedMonth}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to export Excel:', error)
      alert('Failed to export Excel report')
    } finally {
      setExporting(null)
    }
  }

  const handleExportPDF = async () => {
    if (exporting) return
    setExporting('pdf')
    try {
      let url = '/reports/export/pdf?'
      if (filterMode === 'monthly') {
        url += `year=${selectedYear}&month=${selectedMonth}`
      }
      const response = await api.get(url, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `KPI_Report_${selectedYear}_${selectedMonth}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to export PDF:', error)
      alert('Failed to export PDF report')
    } finally {
      setExporting(null)
    }
  }

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const clearUserSelection = () => {
    setSelectedUsers([])
    setUserSearchQuery('')
  }

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    (user.employee_id && user.employee_id.toLowerCase().includes(userSearchQuery.toLowerCase()))
  )

  const getCellColor = (count: number) => {
    if (count === 0) return 'bg-gray-50'
    if (count === 1) return 'bg-green-100 text-green-800'
    if (count === 2) return 'bg-green-200 text-green-900'
    return 'bg-green-300 text-green-900 font-semibold'
  }

  const getPointsCellColor = (points: number) => {
    if (points === 0) return 'bg-gray-50'
    if (points < 5) return 'bg-amber-100 text-amber-800'
    if (points < 10) return 'bg-amber-200 text-amber-900'
    if (points < 15) return 'bg-amber-300 text-amber-900'
    return 'bg-amber-400 text-amber-900 font-semibold'
  }

  const handleIndicatorSelect = (indicatorId: number) => {
    setSelectedIndicator(indicatorId.toString())
    setExpandedEmployee(null)
  }

  const toggleEmployeeExpand = (userId: number) => {
    setExpandedEmployee(prev => prev === userId ? null : userId)
  }

  const isWeekend = (weekday: string) => {
    return weekday === 'Sat' || weekday === 'Sun'
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Jakarta'
    })
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getStatusBadge = (status: string) => {
    const styles: { [key: string]: string } = {
      submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800'
    }
    return styles[status] || styles.draft
  }

  const handleExportSubmissionDetails = () => {
    if (!submissionDetails) return

    // Create PDF document
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const contentWidth = pageWidth - (margin * 2)
    let yPos = 20

    // Helper function to add text and handle page breaks
    const addText = (text: string, fontSize: number = 10, isBold: boolean = false, color: [number, number, number] = [0, 0, 0]) => {
      doc.setFontSize(fontSize)
      doc.setFont('helvetica', isBold ? 'bold' : 'normal')
      doc.setTextColor(color[0], color[1], color[2])

      // Split text to fit width
      const lines = doc.splitTextToSize(text, contentWidth)
      const lineHeight = fontSize * 0.5

      // Check if we need a new page
      if (yPos + (lines.length * lineHeight) > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage()
        yPos = 20
      }

      doc.text(lines, margin, yPos)
      yPos += lines.length * lineHeight + 2
    }

    const addLine = () => {
      if (yPos > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage()
        yPos = 20
      }
      doc.setDrawColor(200, 200, 200)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 5
    }

    const addSpace = (height: number = 5) => {
      yPos += height
      if (yPos > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage()
        yPos = 20
      }
    }

    // Title
    addText('SUBMISSION DETAILS REPORT', 16, true, [2, 66, 109])
    addSpace(3)
    addLine()
    addSpace(5)

    // Employee Info
    addText('Employee Information', 12, true)
    addSpace(2)
    addText(`Name: ${submissionDetails.user.name}`, 10)
    if (submissionDetails.user.employee_id) {
      addText(`Employee ID: ${submissionDetails.user.employee_id}`, 10)
    }
    addText(`Date: ${formatDate(submissionDetails.date)}`, 10)
    addText(`Total Submissions: ${submissionDetails.total_count}`, 10)
    addSpace(5)
    addLine()
    addSpace(5)

    // Submissions
    addText('Submissions', 12, true)
    addSpace(5)

    submissionDetails.submissions.forEach((submission, index) => {
      // Submission header
      addText(`Submission #${index + 1}`, 11, true, [50, 50, 50])
      addSpace(2)

      if (submission.indicator) {
        addText(`KPI Indicator: ${submission.indicator.code} - ${submission.indicator.name}`, 10)
      }

      // Status with color
      const statusColors: { [key: string]: [number, number, number] } = {
        submitted: [59, 130, 246],
        approved: [34, 197, 94],
        rejected: [239, 68, 68],
        draft: [107, 114, 128]
      }
      const statusColor = statusColors[submission.status] || [107, 114, 128]
      addText(`Status: ${submission.status.toUpperCase()}`, 10, false, statusColor)

      addText(`Time: ${formatTime(submission.created_at)}`, 10)

      if (submission.notes) {
        addSpace(2)
        addText('Notes:', 10, true)
        addText(submission.notes, 10, false, [80, 80, 80])
      }

      if (submission.attachments.length > 0) {
        addSpace(2)
        addText(`Attachments (${submission.attachments.length}):`, 10, true)
        submission.attachments.forEach(att => {
          const validIcon = att.ai_is_valid === true ? ' [Valid]' : att.ai_is_valid === false ? ' [Invalid]' : ''
          addText(`  • ${att.original_filename} (${formatFileSize(att.file_size)})${validIcon}`, 9, false, [100, 100, 100])
        })
      }

      addSpace(5)
      if (index < submissionDetails.submissions.length - 1) {
        addLine()
        addSpace(5)
      }
    })

    // Footer
    addSpace(10)
    addLine()
    addSpace(3)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text(`Generated on ${new Date().toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })} - AEON KPI System`, margin, yPos)

    // Save PDF
    const fileName = `Submission_${submissionDetails.user.name.replace(/\s+/g, '_')}_${submissionDetails.date}.pdf`
    doc.save(fileName)

    // Also download all attachments
    submissionDetails.submissions.forEach(submission => {
      submission.attachments.forEach(attachment => {
        const attachmentUrl = `/uploads/${attachment.file_path || 'submissions/' + attachment.filename}`
        const attachmentLink = document.createElement('a')
        attachmentLink.href = attachmentUrl
        attachmentLink.setAttribute('download', attachment.original_filename)
        attachmentLink.setAttribute('target', '_blank')
        document.body.appendChild(attachmentLink)
        attachmentLink.click()
        attachmentLink.remove()
      })
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exporting !== null}
            className="btn-primary flex items-center"
          >
            {exporting === 'excel' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" /> Excel
              </>
            )}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting !== null}
            className="btn-secondary flex items-center"
          >
            {exporting === 'pdf' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" /> PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>

        {/* Filter Mode Toggle */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterMode('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterMode === 'monthly'
                  ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Monthly
            </button>
            <button
              onClick={() => setFilterMode('dateRange')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterMode === 'dateRange'
                  ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Date Range
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-2 border-l pl-4">
            <button
              onClick={() => setViewMode('submissions')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'submissions'
                  ? 'bg-green-100 text-green-700 border-2 border-green-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
              }`}
            >
              <Hash className="h-4 w-4 inline mr-2" />
              Submissions
            </button>
            <button
              onClick={() => setViewMode('points')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'points'
                  ? 'bg-amber-100 text-amber-700 border-2 border-amber-500'
                  : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
              }`}
            >
              <Trophy className="h-4 w-4 inline mr-2" />
              Points
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Filter */}
          {filterMode === 'monthly' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <select
                  className="input w-full"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  className="input w-full"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={idx + 1} value={idx + 1}>{name}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  className="input w-full"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  className="input w-full"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                />
              </div>
            </>
          )}

          {/* KPI Indicator Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="h-4 w-4 inline mr-1" />
              KPI Indicator
            </label>
            <select
              className="input w-full"
              value={selectedIndicator}
              onChange={(e) => setSelectedIndicator(e.target.value)}
            >
              <option value="">All Indicators</option>
              {indicators.map((indicator) => (
                <option key={indicator.id} value={indicator.id}>
                  {indicator.code} - {indicator.name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee Search & Select */}
          <div ref={userSearchRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Users className="h-4 w-4 inline mr-1" />
              Employees
            </label>
            <div
              className="input w-full cursor-pointer flex items-center justify-between"
              onClick={() => setUserSearchOpen(!userSearchOpen)}
            >
              <span className={selectedUsers.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                {selectedUsers.length === 0
                  ? 'All Employees'
                  : selectedUsers.length === 1
                    ? users.find(u => u.id === selectedUsers[0])?.name || '1 selected'
                    : `${selectedUsers.length} selected`
                }
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${userSearchOpen ? 'rotate-180' : ''}`} />
            </div>

            {/* Dropdown */}
            {userSearchOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                {/* Search input */}
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search employees..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                {/* Clear selection button */}
                {selectedUsers.length > 0 && (
                  <div className="px-2 py-1 border-b border-gray-200">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        clearUserSelection()
                      }}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Clear selection
                    </button>
                  </div>
                )}

                {/* User list */}
                <div className="max-h-56 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">No employees found</div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                          selectedUsers.includes(user.id) ? 'bg-primary-50' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleUserSelection(user.id)
                        }}
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          {user.employee_id && (
                            <div className="text-xs text-gray-500">{user.employee_id}</div>
                          )}
                        </div>
                        {selectedUsers.includes(user.id) && (
                          <Check className="h-4 w-4 text-primary-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected employees tags */}
        {selectedUsers.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedUsers.map(userId => {
              const user = users.find(u => u.id === userId)
              return user ? (
                <span
                  key={userId}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                >
                  {user.name}
                  <button
                    onClick={() => toggleUserSelection(userId)}
                    className="hover:bg-primary-200 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : null
            })}
          </div>
        )}
      </div>

      {/* Calendar Grid Report */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading report...</span>
          </div>
        </div>
      ) : reportData ? (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {viewMode === 'submissions' ? 'Submission' : 'Points'} Calendar - {reportData.period.name}
              {reportData.selected_indicator && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({reportData.selected_indicator.code})
                </span>
              )}
            </h3>
            {viewMode === 'submissions' ? (
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-gray-50 border rounded"></div>
                  <span>No submission</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-100 border rounded"></div>
                  <span>1</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-200 border rounded"></div>
                  <span>2</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-300 border rounded"></div>
                  <span>3+</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-gray-50 border rounded"></div>
                  <span>0 pts</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-amber-100 border rounded"></div>
                  <span>&lt;5</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-amber-200 border rounded"></div>
                  <span>&lt;10</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-amber-300 border rounded"></div>
                  <span>&lt;15</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-amber-400 border rounded"></div>
                  <span>15+</span>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Click on any cell with submissions to view details.
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-100 border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900 min-w-[180px]">
                    Employee Name
                  </th>
                  {reportData.dates.map((dateInfo) => (
                    <th
                      key={dateInfo.date}
                      className={`border border-gray-300 px-1 py-2 text-center text-xs font-medium min-w-[36px] ${
                        isWeekend(dateInfo.weekday) ? 'bg-orange-50' : 'bg-gray-100'
                      }`}
                    >
                      <div className="leading-tight">
                        <div className={isWeekend(dateInfo.weekday) ? 'text-orange-600' : 'text-gray-500'}>
                          {dateInfo.weekday}
                        </div>
                        <div className={`font-semibold ${isWeekend(dateInfo.weekday) ? 'text-orange-700' : 'text-gray-900'}`}>
                          {dateInfo.day}
                        </div>
                      </div>
                    </th>
                  ))}
                  <th className={`border border-gray-300 px-3 py-2 text-center text-sm font-semibold text-gray-900 min-w-[70px] ${
                    viewMode === 'points' ? 'bg-amber-50' : 'bg-gray-100'
                  }`}>
                    {viewMode === 'submissions' ? 'Total' : 'Total Pts'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.employees.length === 0 ? (
                  <tr>
                    <td
                      colSpan={reportData.dates.length + 2}
                      className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                    >
                      No employees found
                    </td>
                  </tr>
                ) : (
                  reportData.employees.map((employee, idx) => (
                    <tr key={employee.user.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="sticky left-0 z-10 border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 bg-inherit">
                        <div className="relative">
                          {/* Employee name - clickable when All Indicators and has breakdown */}
                          {!selectedIndicator && employee.indicator_breakdown?.length > 0 ? (
                            <button
                              onClick={() => toggleEmployeeExpand(employee.user.id)}
                              className="employee-name-button text-left w-full hover:text-primary-600 flex items-center gap-1"
                            >
                              <span>{employee.user.name}</span>
                              <ChevronDown className={`h-4 w-4 transition-transform ${expandedEmployee === employee.user.id ? 'rotate-180' : ''}`} />
                            </button>
                          ) : (
                            <div>{employee.user.name}</div>
                          )}
                          {employee.user.employee_id && (
                            <div className="text-xs text-gray-500">{employee.user.employee_id}</div>
                          )}

                          {/* Daily breakdown dropdown */}
                          {expandedEmployee === employee.user.id && !selectedIndicator && (
                            <div className="employee-breakdown-dropdown absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[320px] max-h-[400px] overflow-y-auto">
                              <div className="p-2 bg-gray-50 border-b text-xs font-semibold text-gray-600 sticky top-0 z-10">
                                Daily Submission Breakdown
                              </div>
                              {reportData?.dates
                                .filter(d => (employee.daily_breakdown?.[d.date]?.length || 0) > 0)
                                .map((dateInfo) => {
                                  const dayIndicators = employee.daily_breakdown?.[dateInfo.date] || []
                                  const dayPoints = employee.daily_points?.[dateInfo.date] || 0
                                  return (
                                    <div key={dateInfo.date} className="border-b border-gray-100 last:border-b-0">
                                      <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className={`text-xs font-medium ${isWeekend(dateInfo.weekday) ? 'text-orange-600' : 'text-gray-600'}`}>
                                            {dateInfo.weekday}
                                          </span>
                                          <span className="text-sm font-semibold text-gray-900">
                                            {dateInfo.day}
                                          </span>
                                        </div>
                                        <span className="text-xs font-medium text-amber-600">
                                          +{dayPoints} pts
                                        </span>
                                      </div>
                                      <div className="px-2 py-1">
                                        {dayIndicators.map((item, idx) => (
                                          <button
                                            key={`${item.submission_id}-${idx}`}
                                            onClick={() => {
                                              handleIndicatorSelect(item.indicator_id)
                                              handleCellClick(employee.user.id, dateInfo.date, 1)
                                            }}
                                            className="w-full px-2 py-1.5 text-left hover:bg-primary-50 rounded flex items-center gap-2"
                                          >
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                                              {item.code}
                                            </span>
                                            <span className="text-sm text-gray-700 truncate flex-1">{item.name}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                })}
                              {Object.values(employee.daily_breakdown || {}).every(arr => arr.length === 0) && (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  No submissions yet
                                </div>
                              )}

                              {/* Total Summary */}
                              {employee.indicator_breakdown?.length > 0 && (
                                <div className="p-2 bg-amber-50 border-t sticky bottom-0">
                                  <div className="text-xs font-semibold text-amber-800 mb-1">Total Points by Indicator:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {employee.indicator_breakdown.map((item) => (
                                      <button
                                        key={item.indicator_id}
                                        onClick={() => handleIndicatorSelect(item.indicator_id)}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white border border-amber-200 hover:bg-amber-100"
                                      >
                                        <span className="font-medium text-primary-700">{item.code}</span>
                                        <span className={`font-semibold ${item.points >= item.max_points ? 'text-green-600' : 'text-amber-600'}`}>
                                          {item.points}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      {reportData.dates.map((dateInfo) => {
                        const count = employee.daily_submissions[dateInfo.date] || 0
                        const points = employee.daily_points?.[dateInfo.date] || 0
                        const accumulatedPts = employee.accumulated_points?.[dateInfo.date] || 0
                        const displayValue = viewMode === 'submissions' ? count : points
                        const cellColor = viewMode === 'submissions' ? getCellColor(count) : getPointsCellColor(points)

                        return (
                          <td
                            key={dateInfo.date}
                            onClick={() => handleCellClick(employee.user.id, dateInfo.date, count)}
                            title={viewMode === 'points' ? `Day: ${points} pts | Accumulated: ${accumulatedPts} pts` : undefined}
                            className={`border border-gray-300 px-1 py-2 text-center text-sm ${cellColor} ${
                              isWeekend(dateInfo.weekday) && displayValue === 0 ? 'bg-orange-50' : ''
                            } ${count > 0 ? 'cursor-pointer hover:ring-2 hover:ring-primary-500 hover:ring-inset' : ''}`}
                          >
                            {displayValue > 0 ? displayValue : ''}
                          </td>
                        )
                      })}
                      <td className={`border border-gray-300 px-3 py-2 text-center text-sm font-semibold ${
                        viewMode === 'points' ? 'text-amber-900 bg-amber-100' : 'text-gray-900 bg-blue-50'
                      }`}>
                        {viewMode === 'submissions' ? employee.total_submissions : employee.total_points}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {reportData.employees.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              Showing {reportData.employees.length} employee(s) with{' '}
              {viewMode === 'submissions' ? (
                <>{reportData.employees.reduce((sum, e) => sum + e.total_submissions, 0)} total submissions</>
              ) : (
                <>{reportData.employees.reduce((sum, e) => sum + (e.total_points || 0), 0).toFixed(2)} total points</>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Calendar className="h-12 w-12 mb-4 text-gray-300" />
            <p>Select a date range to view the submission calendar report.</p>
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Submission Details</h3>
                {submissionDetails && (
                  <p className="text-sm text-gray-500">
                    {submissionDetails.user.name} - {formatDate(submissionDetails.date)}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Loading details...</span>
                </div>
              ) : submissionDetails ? (
                <div className="space-y-4 pb-4">
                  {submissionDetails.submissions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No submissions found</p>
                  ) : (
                    submissionDetails.submissions.map((submission) => (
                      <div
                        key={submission.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                      >
                        {/* Submission Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            {submission.indicator && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 mb-2">
                                {submission.indicator.code} - {submission.indicator.name}
                              </span>
                            )}
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="h-4 w-4" />
                              {formatTime(submission.created_at)}
                            </div>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(submission.status)}`}>
                            {submission.status}
                          </span>
                        </div>

                        {/* Notes */}
                        {submission.notes && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                              {submission.notes}
                            </p>
                          </div>
                        )}

                        {/* Attachments */}
                        {submission.attachments.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2">Attachments ({submission.attachments.length})</p>
                            <div className="grid grid-cols-2 gap-2">
                              {submission.attachments.map((attachment) => {
                                const isImage = attachment.file_type?.startsWith('image/')
                                const imageUrl = `/uploads/${attachment.file_path || 'submissions/' + attachment.filename}`

                                return isImage ? (
                                  <div
                                    key={attachment.id}
                                    className="relative group cursor-pointer"
                                    onClick={() => setPreviewImage(imageUrl)}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={attachment.original_filename}
                                      className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-primary-400 transition-colors"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                                      <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
                                        Click to view
                                      </span>
                                    </div>
                                    {attachment.ai_is_valid !== null && (
                                      <span className={`absolute top-1 right-1 text-xs px-1.5 py-0.5 rounded ${
                                        attachment.ai_is_valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {attachment.ai_is_valid ? 'Valid' : 'Invalid'}
                                      </span>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1 truncate">{attachment.original_filename}</p>
                                  </div>
                                ) : (
                                  <button
                                    key={attachment.id}
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      window.open(imageUrl, '_blank', 'noopener,noreferrer')
                                    }}
                                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                                  >
                                    <FileIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-gray-700 truncate">
                                        {attachment.original_filename}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {formatFileSize(attachment.file_size)}
                                      </p>
                                    </div>
                                    {attachment.ai_is_valid !== null && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        attachment.ai_is_valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {attachment.ai_is_valid ? 'Valid' : 'Invalid'}
                                      </span>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Failed to load details</p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  {submissionDetails && `Total: ${submissionDetails.total_count} submission(s)`}
                </p>
                <div className="flex gap-2">
                  {submissionDetails && submissionDetails.submissions.length > 0 && (
                    <button
                      onClick={() => handleExportSubmissionDetails()}
                      className="btn-primary flex items-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </button>
                  )}
                  <button
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            <a
              href={previewImage}
              download
              onClick={(e) => e.stopPropagation()}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4 inline mr-2" />
              Download
            </a>
            <a
              href={previewImage}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Open in New Tab
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
